// src/cost/CostFundingModal.tsx
//
// BekalNanti — Milestone P4: Education Cost + Funding Workspace
// Allows users to map cost items and funding sources for their target goals.
// 100% Deterministic, Mobile-First, Indonesian language.

import React, { useState, useEffect, useMemo } from 'react'
import {
  X,
  Plus,
  Trash2,
  Edit2,
  DollarSign,
  Coins,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Sparkles,
  HelpCircle,
  FileText,
  Building,
} from 'lucide-react'
import { GoalRecord } from '../journey/types'
import {
  CostCategory,
  CostFrequency,
  CertaintyStatus,
  GoalCostItem,
  FundingSourceType,
  FundingStatus,
  GoalFundingSource,
  COST_CATEGORIES,
  FUNDING_TYPES,
  FREQUENCY_LABELS,
  FUNDING_STATUS_LABELS,
  CERTAINTY_LABELS,
  evaluateP4Readiness,
  summarizeCostAndFunding,
} from '../lib/costFunding'
import {
  fetchGoalCostItems,
  createGoalCostItem,
  updateGoalCostItem,
  deleteGoalCostItem,
  fetchGoalFundingSources,
  createGoalFundingSource,
  updateGoalFundingSource,
  deleteGoalFundingSource,
} from '../lib/costFundingService'
import { getGoalDomain } from '../lib/goalDomainRegistry'

interface CostFundingModalProps {
  isOpen: boolean
  onClose: () => void
  goals: GoalRecord[]
  initialGoalId?: string | null
  onSaveSuccess?: () => void
}

export const CostFundingModal: React.FC<CostFundingModalProps> = ({
  isOpen,
  onClose,
  goals,
  initialGoalId,
  onSaveSuccess,
}) => {
  // Support all active goals (not archived)
  const activeGoals = useMemo(() => {
    return goals.filter((g) => g.status !== 'archived')
  }, [goals])

  const [selectedGoalId, setSelectedGoalId] = useState<string>(() => {
    if (initialGoalId && activeGoals.some((g) => g.id === initialGoalId)) {
      return initialGoalId
    }
    return activeGoals[0]?.id || ''
  })
  const [activeTab, setActiveTab] = useState<'costs' | 'funding' | 'review'>('costs')

  const [costItems, setCostItems] = useState<GoalCostItem[]>([])
  const [fundingSources, setFundingSources] = useState<GoalFundingSource[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Cost Item Form State
  const [isAddingCost, setIsAddingCost] = useState<boolean>(false)
  const [editingCostId, setEditingCostId] = useState<string | null>(null)
  const [costCategory, setCostCategory] = useState<CostCategory>('tuition')
  const [costName, setCostName] = useState<string>('')
  const [costAmount, setCostAmount] = useState<string>('')
  const [costCurrency, setCostCurrency] = useState<string>('IDR')
  const [costFrequency, setCostFrequency] = useState<CostFrequency>('per_year')
  const [costCertainty, setCostCertainty] = useState<CertaintyStatus>('estimated')
  const [costNotes, setCostNotes] = useState<string>('')

  // Funding Source Form State
  const [isAddingFunding, setIsAddingFunding] = useState<boolean>(false)
  const [editingFundingId, setEditingFundingId] = useState<string | null>(null)
  const [fundingType, setFundingType] = useState<FundingSourceType>('existing_savings')
  const [fundingName, setFundingName] = useState<string>('')
  const [fundingAmount, setFundingAmount] = useState<string>('')
  const [fundingCurrency, setFundingCurrency] = useState<string>('IDR')
  const [fundingStatus, setFundingStatus] = useState<FundingStatus>('planned')
  const [fundingCertainty, setFundingCertainty] = useState<CertaintyStatus>('known')
  const [fundingNotes, setFundingNotes] = useState<string>('')

  const activeGoal = useMemo(() => {
    return activeGoals.find((g) => g.id === selectedGoalId) || activeGoals[0] || null
  }, [activeGoals, selectedGoalId])

  const domain = useMemo(() => {
    return getGoalDomain(activeGoal?.goalType)
  }, [activeGoal?.goalType])

  // Sync selected goal if initialGoalId or activeGoals change
  useEffect(() => {
    if (initialGoalId && activeGoals.some((g) => g.id === initialGoalId)) {
      setSelectedGoalId(initialGoalId)
    } else if (activeGoals.length > 0) {
      setSelectedGoalId((current) =>
        activeGoals.some((g) => g.id === current) ? current : activeGoals[0]?.id || ''
      )
    }
  }, [initialGoalId, activeGoals])

  // Load cost items & funding sources for active goal
  useEffect(() => {
    if (!isOpen || !activeGoal?.id) return

    let isMounted = true
    setLoading(true)
    setErrorMsg(null)

    Promise.all([
      fetchGoalCostItems(activeGoal.id),
      fetchGoalFundingSources(activeGoal.id),
    ])
      .then(([costs, funding]) => {
        if (!isMounted) return
        setCostItems(costs)
        setFundingSources(funding)
        setLoading(false)
      })
      .catch((err) => {
        if (!isMounted) return
        setErrorMsg(err.message || 'Gagal memuat data biaya dan pendanaan.')
        setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [isOpen, activeGoal?.id])

  if (!isOpen) return null

  if (activeGoals.length === 0 || !activeGoal) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-cost-funding-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto animate-in fade-in duration-200"
      >
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 w-full max-w-md p-6 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto">
            <AlertCircle size={24} />
          </div>
          <div>
            <h2 id="modal-cost-funding-title" className="text-base font-bold text-slate-900">
              Belum Ada Sasaran Aktif
            </h2>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Rumah tangga Anda belum memiliki sasaran rencana aktif. Tambahkan sasaran rencana terlebih dahulu pada dashboard.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors cursor-pointer"
          >
            Tutup
          </button>
        </div>
      </div>
    )
  }

  const readiness = evaluateP4Readiness(costItems, fundingSources, activeGoal.goalType)
  const summary = summarizeCostAndFunding(costItems, fundingSources)

  // Reset Cost Form
  const resetCostForm = () => {
    setIsAddingCost(false)
    setEditingCostId(null)
    setCostCategory((domain.costCategories[0]?.id as CostCategory) || 'tuition')
    setCostName('')
    setCostAmount('')
    setCostFrequency('per_year')
    setCostCertainty('estimated')
    setCostNotes('')
  }

  // Populate Cost Form for Editing
  const startEditCost = (item: GoalCostItem) => {
    setEditingCostId(item.id || null)
    setIsAddingCost(true)
    setCostCategory(item.category)
    setCostName(item.name)
    setCostAmount(item.amount !== null && item.amount !== undefined ? String(item.amount) : '')
    setCostCurrency(item.currency)
    setCostFrequency(item.frequency)
    setCostCertainty(item.certainty)
    setCostNotes(item.notes || '')
  }

  // Save Cost Item
  const handleSaveCost = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!costName.trim()) {
      setErrorMsg('Nama komponen biaya tidak boleh kosong.')
      return
    }

    try {
      setLoading(true)
      const parsedAmount =
        costCertainty === 'unknown' || !costAmount.trim() ? null : Number(costAmount)

      if (editingCostId) {
        const updated = await updateGoalCostItem(editingCostId, {
          category: costCategory,
          name: costName,
          amount: parsedAmount,
          currency: costCurrency,
          frequency: costFrequency,
          certainty: costCertainty,
          notes: costNotes,
        })
        setCostItems((prev) => prev.map((item) => (item.id === editingCostId ? updated : item)))
        setSuccessMsg('Komponen biaya berhasil diperbarui.')
      } else {
        const created = await createGoalCostItem({
          goalId: activeGoal.id,
          category: costCategory,
          name: costName,
          amount: parsedAmount,
          currency: costCurrency,
          frequency: costFrequency,
          certainty: costCertainty,
          notes: costNotes,
        })
        setCostItems((prev) => [...prev, created])
        setSuccessMsg('Komponen biaya berhasil ditambahkan.')
      }

      resetCostForm()
      setLoading(false)
      setTimeout(() => setSuccessMsg(null), 2500)
    } catch (err: any) {
      setLoading(false)
      setErrorMsg(err.message || 'Gagal menyimpan komponen biaya.')
    }
  }

  // Delete Cost Item
  const handleDeleteCost = async (id?: string) => {
    if (!id) return
    try {
      setLoading(true)
      await deleteGoalCostItem(id)
      setCostItems((prev) => prev.filter((item) => item.id !== id))
      setLoading(false)
      setSuccessMsg('Komponen biaya berhasil dihapus.')
      setTimeout(() => setSuccessMsg(null), 2000)
    } catch (err: any) {
      setLoading(false)
      setErrorMsg(err.message || 'Gagal menghapus komponen biaya.')
    }
  }

  // Reset Funding Form
  const resetFundingForm = () => {
    setIsAddingFunding(false)
    setEditingFundingId(null)
    setFundingType('existing_savings')
    setFundingName('')
    setFundingAmount('')
    setFundingStatus('planned')
    setFundingCertainty('known')
    setFundingNotes('')
  }

  // Populate Funding Form for Editing
  const startEditFunding = (src: GoalFundingSource) => {
    setEditingFundingId(src.id || null)
    setIsAddingFunding(true)
    setFundingType(src.sourceType)
    setFundingName(src.name)
    setFundingAmount(src.amount !== null && src.amount !== undefined ? String(src.amount) : '')
    setFundingCurrency(src.currency)
    setFundingStatus(src.status)
    setFundingCertainty(src.certainty)
    setFundingNotes(src.notes || '')
  }

  // Save Funding Source
  const handleSaveFunding = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!fundingName.trim()) {
      setErrorMsg('Nama sumber pendanaan tidak boleh kosong.')
      return
    }

    try {
      setLoading(true)
      const parsedAmount =
        fundingCertainty === 'unknown' || !fundingAmount.trim() ? null : Number(fundingAmount)

      if (editingFundingId) {
        const updated = await updateGoalFundingSource(editingFundingId, {
          sourceType: fundingType,
          name: fundingName,
          amount: parsedAmount,
          currency: fundingCurrency,
          status: fundingStatus,
          isConfirmed: fundingStatus === 'confirmed',
          certainty: fundingCertainty,
          notes: fundingNotes,
        })
        setFundingSources((prev) =>
          prev.map((src) => (src.id === editingFundingId ? updated : src))
        )
        setSuccessMsg('Sumber pendanaan berhasil diperbarui.')
      } else {
        const created = await createGoalFundingSource({
          goalId: activeGoal.id,
          sourceType: fundingType,
          name: fundingName,
          amount: parsedAmount,
          currency: fundingCurrency,
          status: fundingStatus,
          isConfirmed: fundingStatus === 'confirmed',
          certainty: fundingCertainty,
          notes: fundingNotes,
        })
        setFundingSources((prev) => [...prev, created])
        setSuccessMsg('Sumber pendanaan berhasil ditambahkan.')
      }

      resetFundingForm()
      setLoading(false)
      setTimeout(() => setSuccessMsg(null), 2500)
    } catch (err: any) {
      setLoading(false)
      setErrorMsg(err.message || 'Gagal menyimpan sumber pendanaan.')
    }
  }

  // Delete Funding Source
  const handleDeleteFunding = async (id?: string) => {
    if (!id) return
    try {
      setLoading(true)
      await deleteGoalFundingSource(id)
      setFundingSources((prev) => prev.filter((src) => src.id !== id))
      setLoading(false)
      setSuccessMsg('Sumber pendanaan berhasil dihapus.')
      setTimeout(() => setSuccessMsg(null), 2000)
    } catch (err: any) {
      setLoading(false)
      setErrorMsg(err.message || 'Gagal menghapus sumber pendanaan.')
    }
  }

  const handleFinish = () => {
    if (onSaveSuccess) onSaveSuccess()
    onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-cost-funding-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto animate-in fade-in duration-200"
    >
      <div className="bg-white rounded-3xl shadow-xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col my-auto max-h-[92vh]">
        {/* Top Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50/70 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center flex-shrink-0 shadow-2xs mt-0.5">
              <DollarSign size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
                  Milestone P4 • Estimasi Biaya & Pendanaan
                </span>
                {readiness.isReadyForCalculation ? (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                    <CheckCircle2 size={10} />
                    <span>Siap Dihitung (P5)</span>
                  </span>
                ) : (
                  <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                    Sebagian Terpetakan
                  </span>
                )}
              </div>
              <h2
                id="modal-cost-funding-title"
                className="text-base sm:text-lg font-bold text-slate-900 mt-1 leading-snug"
              >
                Estimasi Biaya & Potensi Pendanaan: {activeGoal.name}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Petakan asumsi biaya dan sumber pendanaan yang diketahui tanpa tebak-tebakan.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup modal"
            className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Goal Selector (if user has multiple active goals) */}
        {activeGoals.length > 1 && (
          <div className="px-5 py-2.5 bg-slate-100/70 border-b border-slate-200/70 flex items-center gap-2 overflow-x-auto text-xs">
            <span className="text-slate-400 text-[11px] font-medium flex-shrink-0">
              Pilih Sasaran Tujuan:
            </span>
            <div className="flex items-center gap-1.5">
              {activeGoals.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setSelectedGoalId(g.id)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    g.id === activeGoal.id
                      ? 'bg-white text-teal-800 shadow-2xs border border-teal-200'
                      : 'text-slate-600 hover:bg-slate-200/60'
                  }`}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 bg-white px-5 pt-2 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setActiveTab('costs')}
            className={`pb-2.5 px-3 border-b-2 transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'costs'
                ? 'border-[#0EA5A4] text-teal-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Building size={14} />
            <span>1. Komponen Biaya ({costItems.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('funding')}
            className={`pb-2.5 px-3 border-b-2 transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'funding'
                ? 'border-[#0EA5A4] text-teal-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Coins size={14} />
            <span>2. Sumber Pendanaan ({fundingSources.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('review')}
            className={`pb-2.5 px-3 border-b-2 transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'review'
                ? 'border-[#0EA5A4] text-teal-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FileText size={14} />
            <span>3. Ringkasan Input</span>
          </button>
        </div>

        {/* Messages */}
        {errorMsg && (
          <div className="mx-5 mt-3 p-3 rounded-xl bg-rose-50 border border-rose-200 flex items-center gap-2 text-xs text-rose-700">
            <AlertCircle size={14} className="flex-shrink-0 text-rose-500" />
            <span>{errorMsg}</span>
          </div>
        )}
        {successMsg && (
          <div className="mx-5 mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-2 text-xs text-emerald-800">
            <CheckCircle2 size={14} className="flex-shrink-0 text-emerald-600" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* TAB 1: KOMPONEN BIAYA */}
          {activeTab === 'costs' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    Daftar Komponen Biaya Terpetakan
                  </h3>
                  <p className="text-xs text-slate-500">
                    Masukkan pengeluaran yang diperkirakan diperlukan untuk sasaran ini.
                  </p>
                </div>
                {!isAddingCost && (
                  <button
                    type="button"
                    onClick={() => {
                      resetCostForm()
                      setIsAddingCost(true)
                    }}
                    className="py-1.5 px-3 rounded-xl bg-teal-50 text-teal-700 hover:bg-teal-100 font-semibold text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <Plus size={14} />
                    <span>Tambah Biaya</span>
                  </button>
                )}
              </div>

              {/* Form Add / Edit Cost */}
              {isAddingCost && (
                <form
                  onSubmit={handleSaveCost}
                  className="p-4 rounded-2xl bg-slate-50 border border-teal-200/80 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-teal-900 flex items-center gap-1.5">
                      <Sparkles size={13} className="text-teal-600" />
                      <span>{editingCostId ? 'Perbarui Komponen Biaya' : 'Tambah Komponen Biaya'}</span>
                    </span>
                    <button
                      type="button"
                      onClick={resetCostForm}
                      className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      Batal
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Kategori Biaya
                      </label>
                      <select
                        value={costCategory}
                        onChange={(e) => {
                          const cat = e.target.value as CostCategory
                          setCostCategory(cat)
                          const opt = domain.costCategories.find((c) => c.id === cat) || COST_CATEGORIES.find((c) => c.id === cat)
                          if (opt) setCostFrequency(opt.defaultFrequency)
                        }}
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none"
                      >
                        {(domain.costCategories.length > 0 ? domain.costCategories : COST_CATEGORIES).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Nama / Rincian Biaya
                      </label>
                      <input
                        type="text"
                        required
                        value={costName}
                        onChange={(e) => setCostName(e.target.value)}
                        placeholder={domain.goalType === 'education' ? 'Misal: Uang Kuliah Per Tahun, Sewa Kost' : `Misal: ${domain.costCategories[0]?.typicalItems?.[0] ?? domain.costCategories[0]?.label ?? 'Komponen biaya'}`}
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Tingkat Kepastian
                      </label>
                      <div className="grid grid-cols-3 gap-1">
                        {(['estimated', 'known', 'unknown'] as CertaintyStatus[]).map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setCostCertainty(c)}
                            className={`py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all cursor-pointer ${
                              costCertainty === c
                                ? 'bg-[#0EA5A4] text-white shadow-2xs'
                                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            {CERTAINTY_LABELS[c].split(' ')[0]}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Nominal Jumlah {costCertainty === 'unknown' ? '(Belum Diketahui)' : '(Rp)'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        disabled={costCertainty === 'unknown'}
                        value={costCertainty === 'unknown' ? '' : costAmount}
                        onChange={(e) => setCostAmount(e.target.value)}
                        placeholder={
                          costCertainty === 'unknown'
                            ? 'Jumlah belum diketahui (valid)'
                            : 'Misal: 80000000'
                        }
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Frekuensi / Basis Biaya
                      </label>
                      <select
                        value={costFrequency}
                        onChange={(e) => setCostFrequency(e.target.value as CostFrequency)}
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none"
                      >
                        {Object.entries(FREQUENCY_LABELS).map(([k, label]) => (
                          <option key={k} value={k}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Catatan (Opsional)
                      </label>
                      <input
                        type="text"
                        value={costNotes}
                        onChange={(e) => setCostNotes(e.target.value)}
                        placeholder="Misal: Termasuk makan siang & lab"
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={resetCostForm}
                      className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-semibold cursor-pointer"
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold transition-colors cursor-pointer disabled:opacity-60 flex items-center gap-1.5"
                    >
                      {loading && <Loader2 size={12} className="animate-spin" />}
                      <span>{editingCostId ? 'Simpan Perubahan' : 'Tambahkan Biaya'}</span>
                    </button>
                  </div>
                </form>
              )}

              {/* Cost Items List */}
              {costItems.length === 0 ? (
                <div className="py-8 text-center rounded-2xl border border-dashed border-slate-200 p-6 space-y-2">
                  <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                    <Building size={20} />
                  </div>
                  <p className="text-xs font-semibold text-slate-700">
                    Belum ada komponen biaya yang ditambahkan
                  </p>
                  <p className="text-[11px] text-slate-400 max-w-sm mx-auto">
                    {domain.goalType === 'education'
                      ? 'Mulai dengan menambahkan komponen utama seperti uang kuliah (tuition), biaya hidup, atau visa.'
                      : `Mulai dengan menambahkan komponen utama seperti ${domain.costCategories
                          .slice(0, 3)
                          .map((c) => c.label.toLowerCase())
                          .join(', ')}.`}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {costItems.map((item) => (
                    <div
                      key={item.id}
                      className="p-3.5 rounded-2xl border border-slate-200/80 bg-white hover:border-teal-200 transition-all flex items-center justify-between gap-3 text-xs"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800">{item.name}</span>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                              item.certainty === 'known'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : item.certainty === 'estimated'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}
                          >
                            {CERTAINTY_LABELS[item.certainty]}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2">
                          <span>{FREQUENCY_LABELS[item.frequency]}</span>
                          {item.notes && (
                            <>
                              <span>•</span>
                              <span className="italic">{item.notes}</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="text-xs font-bold text-slate-900 block">
                            {item.certainty === 'unknown' || item.amount === null
                              ? 'Belum diketahui'
                              : `${item.currency} ${Number(item.amount).toLocaleString('id-ID')}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => startEditCost(item)}
                            className="p-1 rounded-lg text-slate-400 hover:text-teal-700 hover:bg-slate-100 cursor-pointer"
                            aria-label="Edit biaya"
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteCost(item.id)}
                            className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer"
                            aria-label="Hapus biaya"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: SUMBER PENDANAAN */}
          {activeTab === 'funding' && (
            <div className="space-y-4">
              {/* Distinct P2 / P4 Isolation Notice */}
              <div className="p-3 rounded-2xl bg-teal-50/70 border border-teal-200 text-xs text-teal-900 flex items-start gap-2.5">
                <HelpCircle size={16} className="text-teal-700 flex-shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <p className="font-semibold text-teal-950">Prinsip Pemetaan Sumber Pendanaan</p>
                  <p className="text-[11px] text-teal-800 leading-relaxed">
                    Sumber pendanaan di sini mencatat potensi atau rencana bekal untuk sasaran ini (seperti beasiswa, bantuan keluarga, atau alokasi simpanan).
                    Mencatat alokasi di sini <strong>tidak</strong> akan otomatis menduplikasi atau mengubah saldo tabungan pada data Keuangan Keluarga.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    Daftar Sumber Pendanaan Terpetakan
                  </h3>
                  <p className="text-xs text-slate-500">
                    Catat potensi beasiswa, hibah, atau simpanan yang direncanakan.
                  </p>
                </div>
                {!isAddingFunding && (
                  <button
                    type="button"
                    onClick={() => {
                      resetFundingForm()
                      setIsAddingFunding(true)
                    }}
                    className="py-1.5 px-3 rounded-xl bg-teal-50 text-teal-700 hover:bg-teal-100 font-semibold text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <Plus size={14} />
                    <span>Tambah Sumber</span>
                  </button>
                )}
              </div>

              {/* Form Add / Edit Funding */}
              {isAddingFunding && (
                <form
                  onSubmit={handleSaveFunding}
                  className="p-4 rounded-2xl bg-slate-50 border border-teal-200/80 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-teal-900 flex items-center gap-1.5">
                      <Sparkles size={13} className="text-teal-600" />
                      <span>{editingFundingId ? 'Perbarui Sumber Pendanaan' : 'Tambah Sumber Pendanaan'}</span>
                    </span>
                    <button
                      type="button"
                      onClick={resetFundingForm}
                      className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      Batal
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Jenis Sumber Pendanaan
                      </label>
                      <select
                        value={fundingType}
                        onChange={(e) => setFundingType(e.target.value as FundingSourceType)}
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none"
                      >
                        {FUNDING_TYPES.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Nama Sumber Pendanaan
                      </label>
                      <input
                        type="text"
                        required
                        value={fundingName}
                        onChange={(e) => setFundingName(e.target.value)}
                        placeholder="Misal: Beasiswa LPDP, Tabungan Bekal Anak"
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Tingkat Kepastian
                      </label>
                      <div className="grid grid-cols-3 gap-1">
                        {(['known', 'estimated', 'unknown'] as CertaintyStatus[]).map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setFundingCertainty(c)}
                            className={`py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all cursor-pointer ${
                              fundingCertainty === c
                                ? 'bg-[#0EA5A4] text-white shadow-2xs'
                                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            {CERTAINTY_LABELS[c].split(' ')[0]}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Nominal Potensi {fundingCertainty === 'unknown' ? '(Belum Diketahui)' : '(Rp)'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        disabled={fundingCertainty === 'unknown'}
                        value={fundingCertainty === 'unknown' ? '' : fundingAmount}
                        onChange={(e) => setFundingAmount(e.target.value)}
                        placeholder={
                          fundingCertainty === 'unknown'
                            ? 'Jumlah belum diketahui (valid)'
                            : 'Misal: 50000000'
                        }
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Status Saat Ini
                      </label>
                      <select
                        value={fundingStatus}
                        onChange={(e) => setFundingStatus(e.target.value as FundingStatus)}
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none"
                      >
                        {Object.entries(FUNDING_STATUS_LABELS).map(([k, label]) => (
                          <option key={k} value={k}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        Catatan (Opsional)
                      </label>
                      <input
                        type="text"
                        value={fundingNotes}
                        onChange={(e) => setFundingNotes(e.target.value)}
                        placeholder="Misal: Syarat IPK 3.25, pendaftaran 2029"
                        className="w-full p-2 rounded-xl border border-slate-200 bg-white font-medium focus:ring-2 focus:ring-[#0EA5A4] focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={resetFundingForm}
                      className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-semibold cursor-pointer"
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold transition-colors cursor-pointer disabled:opacity-60 flex items-center gap-1.5"
                    >
                      {loading && <Loader2 size={12} className="animate-spin" />}
                      <span>{editingFundingId ? 'Simpan Perubahan' : 'Tambahkan Sumber'}</span>
                    </button>
                  </div>
                </form>
              )}

              {/* Funding Sources List */}
              {fundingSources.length === 0 ? (
                <div className="py-8 text-center rounded-2xl border border-dashed border-slate-200 p-6 space-y-2">
                  <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                    <Coins size={20} />
                  </div>
                  <p className="text-xs font-semibold text-slate-700">
                    Belum ada sumber pendanaan yang dicatat
                  </p>
                  <p className="text-[11px] text-slate-400 max-w-sm mx-auto">
                    Tambahkan beasiswa, bantuan keluarga, atau tabungan yang direncanakan untuk sasaran ini.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {fundingSources.map((src) => (
                    <div
                      key={src.id}
                      className="p-3.5 rounded-2xl border border-slate-200/80 bg-white hover:border-teal-200 transition-all flex items-center justify-between gap-3 text-xs"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800">{src.name}</span>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                              src.certainty === 'known'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : src.certainty === 'estimated'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}
                          >
                            {CERTAINTY_LABELS[src.certainty]}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold border border-teal-200">
                            {FUNDING_STATUS_LABELS[src.status]}
                          </span>
                        </div>
                        {src.notes && (
                          <div className="text-[11px] text-slate-500 italic">{src.notes}</div>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="text-xs font-bold text-slate-900 block">
                            {src.certainty === 'unknown' || src.amount === null
                              ? 'Belum diketahui'
                              : `${src.currency} ${Number(src.amount).toLocaleString('id-ID')}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => startEditFunding(src)}
                            className="p-1 rounded-lg text-slate-400 hover:text-teal-700 hover:bg-slate-100 cursor-pointer"
                            aria-label="Edit pendanaan"
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteFunding(src.id)}
                            className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer"
                            aria-label="Hapus pendanaan"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: REVIEW / RINGKASAN INPUT */}
          {activeTab === 'review' && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">
                    Rekapitulasi Data Input Pemetaan
                  </span>
                  <span
                    className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                      readiness.isReadyForCalculation
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {readiness.isReadyForCalculation ? 'Siap Dihitung' : 'Parsial'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="p-3 rounded-xl bg-white border border-slate-200 space-y-1">
                    <span className="text-slate-500 text-[11px] block">
                      Total Input Biaya yang Dipetakan
                    </span>
                    <span className="text-sm sm:text-base font-extrabold text-slate-900 block">
                      Rp {summary.totalCostsMapped.toLocaleString('id-ID')}
                    </span>
                    <span className="text-[10px] text-slate-400 block">
                      {summary.costItemsCount} komponen ({summary.unknownCostsCount} belum diketahui)
                    </span>
                  </div>

                  <div className="p-3 rounded-xl bg-white border border-slate-200 space-y-1">
                    <span className="text-slate-500 text-[11px] block">
                      Total Potensi Pendanaan Dicatat
                    </span>
                    <span className="text-sm sm:text-base font-extrabold text-teal-800 block">
                      Rp {summary.totalFundingMapped.toLocaleString('id-ID')}
                    </span>
                    <span className="text-[10px] text-slate-400 block">
                      {summary.fundingSourcesCount} sumber ({summary.unknownFundingCount} belum diketahui)
                    </span>
                  </div>
                </div>

                {/* Important Strict Boundary Disclaimer */}
                <div className="p-3 rounded-xl bg-amber-50/70 border border-amber-200/80 text-[11px] text-amber-900 leading-relaxed">
                  <strong>Pemberitahuan Penting:</strong> Rekapitulasi di atas semata-mata merupakan ringkasan data input yang Anda petakan, bukan hasil kalkulasi kelayakan finansial atau rekomendasi. Proyeksi nilai masa depan, inflasi pendidikan, dan analisis kapasitas tabungan akan dihitung secara deterministik pada tahap <strong>Perhitungan Kebutuhan Bekal</strong>.
                </div>
              </div>

              {/* Ready notice */}
              {readiness.isReadyForCalculation ? (
                <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-start gap-2.5 text-xs text-emerald-900">
                  <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">Sasaran Telah Siap untuk Tahap Perhitungan</p>
                    <p className="text-[11px] text-emerald-800 mt-0.5">
                      Anda telah memetakan struktur biaya yang memadai. Anda dapat melanjutkan ke Perencanaan untuk menjalankan kalkulasi perencanaan.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-3.5 rounded-2xl bg-slate-100 border border-slate-200 text-xs text-slate-600">
                  <p className="font-semibold text-slate-800">Pemetaan Belum Lengkap</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {readiness.missingRequirements[0] || 'Tambahkan setidaknya satu komponen biaya untuk melanjutkan.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/70 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-semibold text-xs transition-colors cursor-pointer"
          >
            Tutup
          </button>

          <div className="flex items-center gap-2">
            {activeTab === 'costs' && (
              <button
                type="button"
                onClick={() => setActiveTab('funding')}
                className="px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs shadow-xs transition-all cursor-pointer"
              >
                Lanjut ke Pendanaan &rarr;
              </button>
            )}
            {activeTab === 'funding' && (
              <button
                type="button"
                onClick={() => setActiveTab('review')}
                className="px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs shadow-xs transition-all cursor-pointer"
              >
                Lanjut ke Ringkasan &rarr;
              </button>
            )}
            {activeTab === 'review' && (
              <button
                type="button"
                onClick={handleFinish}
                className="px-5 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs shadow-sm transition-all cursor-pointer"
              >
                Selesai & Simpan
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
